import { PublicPageShell } from '@/components/layout/public-page-shell';
import { CompletionPreview } from '@/features/preboarding-forms/wizard/completion-preview';
import { WelcomePreview } from '@/features/preboarding-forms/wizard/welcome-preview';
import { EmailGate } from '@/features/preboarding-forms/runtime/email-gate';
import { PreboardingError } from '@/features/preboarding-forms/runtime/preboarding-error';
import { SectionStepper } from '@/features/preboarding-forms/runtime/section-stepper';
import {
  getPublicPreboardingForm,
  submitPreboardingForm,
  validatePreboardingToken,
  type InvalidInvitationReason,
} from '@/lib/preboarding-public';
import type { PublicPreboardingForm } from '@hygge/shared';
import { createFileRoute } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

// Public, unauthenticated runtime for a new hire to fill a pre-boarding form.
// No `beforeLoad` — that omission is what bypasses the app's auth redirect.
export const Route = createFileRoute('/preboarding/$token')({
  component: PreboardingRuntime,
});

type Phase = 'loading' | 'invalid' | 'gate' | 'runtime';
// Runtime step: the welcome screen, a section by index, or the completion screen.
type Step = 'welcome' | number | 'completion';

function PreboardingRuntime() {
  const { token } = Route.useParams();

  const [phase, setPhase] = useState<Phase>('loading');
  const [reason, setReason] = useState<InvalidInvitationReason | undefined>();
  const [form, setForm] = useState<PublicPreboardingForm | null>(null);

  const [gateSubmitting, setGateSubmitting] = useState(false);
  const [gateError, setGateError] = useState<string | null>(null);
  // The email proven at the gate — re-sent with the submission.
  const [verifiedEmail, setVerifiedEmail] = useState('');

  const [step, setStep] = useState<Step>('welcome');
  const [answers, setAnswers] = useState<Record<string, string | boolean>>({});
  const [files, setFiles] = useState<Record<string, File | null>>({});
  const [gdprConsent, setGdprConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    validatePreboardingToken(token)
      .then((result) => {
        if (!active) return;
        if (result.valid) {
          setPhase('gate');
        } else {
          setReason(result.reason);
          setPhase('invalid');
        }
      })
      .catch(() => {
        if (active) setPhase('invalid');
      });
    return () => {
      active = false;
    };
  }, [token]);

  const handleGateSubmit = async (email: string) => {
    setGateSubmitting(true);
    setGateError(null);
    try {
      const payload = await getPublicPreboardingForm(token, email);
      setForm(payload);
      setVerifiedEmail(email);
      setStep(initialStep(payload));
      setPhase('runtime');
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) {
        setGateError("That email doesn't match this invitation. Please try again.");
      } else if (status === 404) {
        setPhase('invalid');
      } else {
        setGateError('Something went wrong. Please try again.');
      }
    } finally {
      setGateSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      await submitPreboardingForm(token, { email: verifiedEmail, answers, files, gdprConsent });
      setStep('completion');
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      setSubmitError(message ?? 'Something went wrong submitting the form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === 'loading') {
    return (
      <PublicPageShell>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-hygge" />
        </div>
      </PublicPageShell>
    );
  }

  if (phase === 'invalid') {
    return (
      <PublicPageShell>
        <PreboardingError reason={reason} />
      </PublicPageShell>
    );
  }

  if (phase === 'gate') {
    return (
      <PublicPageShell>
        <EmailGate onSubmit={handleGateSubmit} submitting={gateSubmitting} error={gateError} />
      </PublicPageShell>
    );
  }

  // phase === 'runtime'
  if (!form) return null;
  const sections = form.sections;

  return (
    <PublicPageShell>
      {step === 'welcome' && (
        <div className="mx-auto w-full max-w-[560px] px-4 py-10">
          <WelcomePreview
            coverImageUrl={form.welcomeConfig?.coverImageUrl ?? null}
            greetingTitle={form.welcomeConfig?.greetingTitle ?? ''}
            welcomeMessage={form.welcomeConfig?.welcomeMessage ?? ''}
            onStart={() => setStep(sections.length > 0 ? 0 : 'completion')}
          />
        </div>
      )}

      {typeof step === 'number' && sections[step] && (
        <SectionStepper
          section={sections[step]}
          sectionNumber={step + 1}
          totalSections={sections.length}
          isLast={step === sections.length - 1}
          answers={answers}
          files={files}
          onChange={(id, value) => setAnswers((prev) => ({ ...prev, [id]: value }))}
          onFileChange={(id, file) => setFiles((prev) => ({ ...prev, [id]: file }))}
          onBack={() => setStep(step === 0 ? 'welcome' : step - 1)}
          onNext={() => setStep(step + 1)}
          onSubmit={handleSubmit}
          gdprConsent={gdprConsent}
          onGdprChange={setGdprConsent}
          submitting={submitting}
          submitError={submitError}
        />
      )}

      {step === 'completion' && (
        <div className="mx-auto w-full max-w-[560px] px-4 py-16">
          <CompletionPreview
            successTitle={form.completionConfig?.successTitle ?? ''}
            closingMessage={form.completionConfig?.closingMessage ?? ''}
            feedbackLink={
              form.completionConfig?.feedbackLink ?? { enabled: false, label: '', url: '' }
            }
            interactive
          />
        </div>
      )}
    </PublicPageShell>
  );
}

// Skip an empty welcome screen (no greeting/message configured) so a candidate
// lands straight on the first section; fall back to completion if there are no
// sections at all.
function initialStep(form: PublicPreboardingForm): Step {
  const w = form.welcomeConfig;
  const hasWelcome = !!w && (w.greetingTitle.trim() !== '' || w.welcomeMessage.trim() !== '');
  if (hasWelcome) return 'welcome';
  return form.sections.length > 0 ? 0 : 'completion';
}
